import { Users, Shield, LayoutDashboard, Bell, LogOut, Monitor, Settings, Sun, Moon, Key, FileBarChart, Crown, ChevronDown, Layers } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import React from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@/shared/hooks/useTheme";
import { ViewSwitcher } from "@/shared/components/ViewSwitcher";
import { LanguageMenu } from "@/shared/components/LanguageMenu";

import {
  Sidebar,
  SidebarContent,
  SidebarTrigger,
} from "@/shared/components/ui/sidebar";
import { Button } from "@/shared/components/ui/button";
import { useSidebar } from "@/shared/hooks/useSidebar";
import { useAuth } from "@/shared/hooks/useAuth";
import { usePermissionCheck } from "@/shared/hooks/usePermissionCheck";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/shared/components/ui/tooltip";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/shared/components/ui/collapsible";

type NavChild = { title: string; url: string };
type NavItem = {
  title: string;
  url: string;
  icon: typeof LayoutDashboard;
  requireAdmin?: boolean;
  requireTenant?: boolean;
  // Itens operacionais somem na visao Plataforma (visoes estritas).
  operational?: boolean;
  isVisible?: () => boolean;
  children?: NavChild[];
};

const ForwardedLink = React.forwardRef<HTMLAnchorElement, React.ComponentProps<typeof Link>>(
  (props, ref) => <Link ref={ref} {...props} />
);
ForwardedLink.displayName = "ForwardedLink";

const ForwardedButton = React.forwardRef<HTMLButtonElement, React.ComponentProps<"button">>(
  (props, ref) => <button ref={ref} {...props} />
);
ForwardedButton.displayName = "ForwardedButton";

export function AppSidebar() {
  const { t } = useTranslation('common');
  const { state, isMobile } = useSidebar();
  const { user, logout, isAdmin, isTenant, isOperador } = useAuth();
  const { canViewInstallationTokens, isPlatformAdmin } = usePermissionCheck();
  const location = useLocation();
  const { setTheme, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  // Visoes estritas: na visao Plataforma (platform admin sem conta assumida) o
  // menu mostra so administracao; em visao assumida, so a operacao da conta.
  const isActingView = Boolean(user?.actingTenantId);
  const isPlatformView = isPlatformAdmin() && !isActingView;

  const navigationItems: NavItem[] = [
    { title: t('nav.dashboard'), url: "/", icon: LayoutDashboard, operational: true },
    { title: t('nav.accounts'), url: "/contas", icon: Users, requireTenant: true, operational: true },
    { title: t('nav.devices'), url: "/devices", icon: Monitor, operational: true },
    { title: t('nav.groups'), url: "/grupos", icon: Layers, isVisible: () => isAdmin() || isOperador(), operational: true },
    { title: t('nav.alerts'), url: "/alertas", icon: Bell, operational: true },
    { title: t('nav.reports'), url: "/relatorios", icon: FileBarChart, operational: true },
    { title: t('nav.installationTokens'), url: "/tokens-instalacao", icon: Key, isVisible: canViewInstallationTokens, operational: true },
    { title: t('nav.accessControl'), url: "/controle-de-acesso", icon: Shield, requireAdmin: true },
    {
      title: t('nav.administration'),
      url: "/administracao/evolucao",
      icon: Crown,
      isVisible: () => isPlatformAdmin() && !isActingView,
      children: [
        { title: t('nav.administrationEvolution'), url: "/administracao/evolucao" },
        { title: t('nav.tenantManagement'), url: "/administracao/tenants" },
      ],
    },
    { title: t('nav.settings'), url: "/configuracoes", icon: Settings },
  ];

  const getUserInitial = () => {
    if (user?.name) {
      return user.name.charAt(0).toUpperCase();
    }
    return 'U';
  };

  const isActiveRoute = (url: string) => {
    if (url === "/") {
      return location.pathname === "/" || location.pathname === "/dashboard";
    }
    // Rotas de usuários fazem parte do Controle de Acesso
    if (url === "/controle-de-acesso" && location.pathname.startsWith("/usuarios")) {
      return true;
    }
    return location.pathname === url || location.pathname.startsWith(url + "/");
  };

  const filteredNavigationItems = navigationItems.filter(item => {
    if (isPlatformView && item.operational) {
      return false;
    }
    if (item.isVisible) {
      return item.isVisible();
    }
    if (item.requireTenant && !isTenant()) {
      return false;
    }
    if (item.requireAdmin && !isAdmin()) {
      return false;
    }
    return true;
  });

  return (
    <TooltipProvider>
      <Sidebar className="border-r border-sidebar-border data-[state=collapsed]:w-20" collapsible={isMobile ? "offcanvas" : "icon"}>
      <SidebarContent>
        {state === "expanded" || isMobile ? (
          <div className="h-16 px-3 py-4 border-b border-sidebar-border flex items-center justify-between">
            <div className="flex items-center justify-center w-full">
              <img
                src={isDark ? "/logo-invexis-neon.png" : "/logo-invexis-dark.png"}
                alt="Invexis"
                className="h-10 w-auto"
              />
            </div>
            {isMobile && <SidebarTrigger className="ml-auto" />}
          </div>
        ) : (
          <div className="h-16 border-b border-sidebar-border flex items-center">
            <div className="flex justify-center w-full">
              <img
                src={isDark ? "/icon-invexis.png" : "/icon-invexis-dark.png"}
                alt="Invexis"
                className="h-10 w-auto"
              />
            </div>
          </div>
        )}

        <nav className="flex-1 py-4 px-2 space-y-1 overflow-y-auto">
          {filteredNavigationItems.map((item) => (
            <div key={item.title} tabIndex={0}>
              {item.children ? (
                state === "collapsed" && !isMobile ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <ForwardedLink
                        to={item.url}
                        className={`flex items-center justify-center rounded-lg py-3 px-3 transition-colors duration-200 ${
                          item.children.some(c => isActiveRoute(c.url))
                            ? "bg-primary/10 text-primary border-l-2 border-primary"
                            : "text-sidebar-foreground hover:bg-sidebar-accent"
                        }`}
                      >
                        <item.icon className={`w-5 h-5 flex-shrink-0 ${item.children.some(c => isActiveRoute(c.url)) ? 'text-primary' : ''}`} />
                      </ForwardedLink>
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      <p>{item.title}</p>
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  <Collapsible defaultOpen={item.children.some(c => isActiveRoute(c.url))}>
                    <CollapsibleTrigger className="flex items-center gap-3 px-3 py-2.5 w-full rounded-lg text-sidebar-foreground hover:bg-sidebar-accent">
                      <item.icon className="w-5 h-5 flex-shrink-0" />
                      <span className="font-medium whitespace-nowrap flex-1 text-left">{item.title}</span>
                      <ChevronDown className="w-4 h-4" />
                    </CollapsibleTrigger>
                    <CollapsibleContent className="pl-6">
                      {item.children.map(child => (
                        <ForwardedLink
                          key={child.url}
                          to={child.url}
                          className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                            isActiveRoute(child.url)
                              ? "bg-primary/10 text-primary border-l-2 border-primary"
                              : "text-sidebar-foreground hover:bg-sidebar-accent"
                          }`}
                        >
                          <span className="font-medium whitespace-nowrap">{child.title}</span>
                        </ForwardedLink>
                      ))}
                    </CollapsibleContent>
                  </Collapsible>
                )
              ) : (
                state === "collapsed" && !isMobile ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <ForwardedLink
                        to={item.url}
                        className={`flex items-center justify-center rounded-lg py-3 px-3 transition-colors duration-200 ${
                          isActiveRoute(item.url)
                            ? "bg-primary/10 text-primary border-l-2 border-primary"
                            : "text-sidebar-foreground hover:bg-sidebar-accent"
                        }`}
                      >
                        <item.icon className={`w-5 h-5 flex-shrink-0 ${isActiveRoute(item.url) ? 'text-primary' : ''}`} />
                      </ForwardedLink>
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      <p>{item.title}</p>
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  <ForwardedLink
                    to={item.url}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors duration-200 ${
                      isActiveRoute(item.url)
                        ? "bg-primary/10 text-primary border-l-2 border-primary"
                        : "text-sidebar-foreground hover:bg-sidebar-accent"
                    }`}
                  >
                    <div>
                      <item.icon className={`w-5 h-5 flex-shrink-0 ${isActiveRoute(item.url) ? 'text-primary' : ''}`} />
                    </div>
                    <span className="font-medium whitespace-nowrap">{item.title}</span>
                  </ForwardedLink>
                )
              )}
            </div>
          ))}
        </nav>

        <div className="mt-auto p-3 border-t border-sidebar-border space-y-2">
          {state === "expanded" || isMobile ? (
            <>
              <div className="flex items-center justify-center gap-2 mb-2">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <ForwardedButton
                      onClick={() => setTheme(isDark ? "light" : "dark")}
                      aria-label={isDark ? t('sidebar.lightMode') : t('sidebar.darkMode')}
                      className="flex items-center justify-center p-2 rounded-lg hover:bg-sidebar-accent/50 transition-colors"
                    >
                      {isDark ? (
                        <Sun className="h-5 w-5 text-sidebar-foreground" />
                      ) : (
                        <Moon className="h-5 w-5 text-sidebar-foreground" />
                      )}
                    </ForwardedButton>
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    <p>{isDark ? t('sidebar.lightMode') : t('sidebar.darkMode')}</p>
                  </TooltipContent>
                </Tooltip>
                <LanguageMenu />
                {!isMobile && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <SidebarTrigger className="flex items-center justify-center p-2" />
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      <p>{t('sidebar.collapseMenu')}</p>
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>

              <div className="mb-2">
                <ViewSwitcher />
              </div>

              <div className="flex items-center gap-3 p-2 rounded-lg bg-sidebar-accent">
                <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                  <span className="text-primary text-sm font-medium">{getUserInitial()}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-sidebar-foreground truncate">{user?.name || t('sidebar.userFallback')}</p>
                  <p className="text-xs text-muted-foreground truncate">{user?.email || t('sidebar.emailFallback')}</p>
                </div>
                <div>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={logout}
                        className="flex-shrink-0 h-8 w-8"
                      >
                        <LogOut className="w-4 h-4 text-muted-foreground" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      <p>{t('sidebar.logout')}</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <ForwardedButton
                    onClick={() => setTheme(isDark ? "light" : "dark")}
                    aria-label={isDark ? t('sidebar.lightMode') : t('sidebar.darkMode')}
                    className="flex items-center justify-center p-2 rounded-lg hover:bg-sidebar-accent/50 transition-colors"
                  >
                    {isDark ? (
                      <Sun className="h-5 w-5 text-sidebar-foreground" />
                    ) : (
                      <Moon className="h-5 w-5 text-sidebar-foreground" />
                    )}
                  </ForwardedButton>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p>{isDark ? t('sidebar.lightMode') : t('sidebar.darkMode')}</p>
                </TooltipContent>
              </Tooltip>

              <LanguageMenu />

              <Tooltip>
                <TooltipTrigger asChild>
                  <SidebarTrigger className="flex items-center justify-center w-full py-3 px-3" />
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p>{t('sidebar.expandMenu')}</p>
                </TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center cursor-default">
                    <span className="text-primary text-sm font-medium">{getUserInitial()}</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p>{user?.name || t('sidebar.userFallback')}</p>
                </TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={logout}
                    className="h-8 w-8"
                  >
                    <LogOut className="w-4 h-4 text-muted-foreground" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p>{t('sidebar.logout')}</p>
                </TooltipContent>
              </Tooltip>
            </div>
          )}
        </div>
      </SidebarContent>
      </Sidebar>
    </TooltipProvider>
  );
}
